import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Bell, Loader2 } from "lucide-react";
import {
  useNotificationPreferences,
  useUpdateNotificationPreferences,
} from "@/hooks/useNotificationSettings";

interface CalendarNotificationSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface CalendarPrefs {
  calendar_event_created: boolean;
  calendar_event_updated: boolean;
  calendar_event_reminder: boolean;
  calendar_event_cancelled: boolean;
}

const DEFAULT_PREFS: CalendarPrefs = {
  calendar_event_created: true,
  calendar_event_updated: true,
  calendar_event_reminder: true,
  calendar_event_cancelled: true,
};

const SETTINGS: Array<{
  key: keyof CalendarPrefs;
  label: string;
  description: string;
}> = [
  {
    key: "calendar_event_created",
    label: "Event Created",
    description: "Notify me when a new event is added to a calendar I follow.",
  },
  {
    key: "calendar_event_updated",
    label: "Event Updated",
    description: "Notify me when an event I care about is modified.",
  },
  {
    key: "calendar_event_reminder",
    label: "Event Reminders",
    description: "Send a reminder before an upcoming event starts.",
  },
  {
    key: "calendar_event_cancelled",
    label: "Event Cancelled",
    description: "Notify me when a scheduled event is cancelled.",
  },
];

export function CalendarNotificationSettingsDialog({
  open,
  onOpenChange,
}: CalendarNotificationSettingsDialogProps) {
  const { data: preferences, isLoading } = useNotificationPreferences();
  const updatePrefs = useUpdateNotificationPreferences();

  const [localPrefs, setLocalPrefs] = useState<CalendarPrefs>(DEFAULT_PREFS);

  // Sync from server whenever dialog opens
  useEffect(() => {
    if (open && preferences) {
      const stored = preferences?.email || {};
      setLocalPrefs({
        calendar_event_created:  stored.calendar_event_created  ?? true,
        calendar_event_updated:  stored.calendar_event_updated  ?? true,
        calendar_event_reminder: stored.calendar_event_reminder ?? true,
        calendar_event_cancelled:stored.calendar_event_cancelled?? true,
      });
    }
  }, [open, preferences]);

  const handleToggle = (key: keyof CalendarPrefs, value: boolean) => {
    setLocalPrefs((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = () => {
    const merged = {
      ...preferences,
      email: {
        ...(preferences?.email || {}),
        ...localPrefs,
      },
    };
    updatePrefs.mutate(merged, {
      onSuccess: () => onOpenChange(false),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Calendar Notifications
          </DialogTitle>
          <DialogDescription>
            Choose which calendar events you want to be notified about via email.
            These settings apply only to your account.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-5 py-2">
            {SETTINGS.map(({ key, label, description }) => (
              <div key={key} className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <Label htmlFor={key} className="text-sm font-medium cursor-pointer">
                    {label}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {description}
                  </p>
                </div>
                <Switch
                  id={key}
                  checked={localPrefs[key]}
                  onCheckedChange={(v) => handleToggle(key, v)}
                />
              </div>
            ))}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={updatePrefs.isPending || isLoading}
          >
            {updatePrefs.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : null}
            Save Preferences
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
